import React, { useEffect, useState } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Dimensions,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";

const CustomerOrderSummaryReport = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { order } = route.params as { order: any };

  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );
  const isMobile = windowWidth < 768;

  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) =>
      setWindowWidth(window.width),
    );
    return () => sub?.remove();
  }, []);

  if (!order) return null;

  return (
    <ScrollView style={styles.page}>
      {/* HEADER */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <AntDesign name="arrowleft" size={24} color="#000" />
        </TouchableOpacity>

        <View style={styles.headerRight}>
          <Text style={styles.orderNo}>{order.orderNo}</Text>
          <View style={styles.statusChip}>
            <Text style={styles.statusText}>{order.orderStatus}</Text>
          </View>
        </View>
      </View>

      {/* ORDER META */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Order Information</Text>

        <MetaRow label="Organization" value={order.organization?.name} />
        <MetaRow label="Sub Organization" value={order.subOrganization?.name} />
        <MetaRow label="Request Type" value={order.requestType} />
        <MetaRow label="Created By" value={order.createdBy?.auth?.email} />
        <MetaRow
          label="Created At"
          value={new Date(order.createdAt).toLocaleString()}
        />
      </View>

      {/* ITEMS */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Order Items</Text>

        {order?.details?.map((item: any) => {
          const appointment = item.appointments?.[0];

          return (
            <View
              key={item._id}
              style={[styles.itemRow, isMobile && styles.itemRowMobile]}
            >
              <Image
                source={{ uri: item.imageUrl }}
                style={styles.productImage}
              />

              <View style={styles.itemContent}>
                <Text style={styles.itemCode}>{item.itemCode}</Text>
                <Text style={styles.itemName}>{item.itemName}</Text>

                <Text style={styles.meta}>
                  Qty: {item.quantity} {item.uom}
                </Text>
                <Text style={styles.meta}>
                  Warehouse: {item.warehouse?.name}
                </Text>
                <Text style={styles.meta}>
                  Required Date:{" "}
                  {new Date(item.requiredDate).toLocaleDateString()}
                </Text>
                <Text style={styles.meta}>
                  Per Carton: {item.perCtns} | Per Pallet: {item.perPallet}
                </Text>

                {item.documentNumber && (
                  <Text style={styles.meta}>
                    Document Number: {item.documentNumber}
                  </Text>
                )}

                {appointment && (
                  <Text style={styles.appointment}>
                    Appointment:{" "}
                    {new Date(appointment.startDateTime).toLocaleString()} –{" "}
                    {new Date(appointment.endDateTime).toLocaleTimeString()}
                  </Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

const MetaRow = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.metaRow}>
    <Text style={styles.metaLabel}>{label}</Text>
    <Text style={styles.metaValue}>{value || "-"}</Text>
  </View>
);

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#f5f6f8",
    padding: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  headerRight: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginLeft: 12,
  },
  orderNo: {
    fontSize: 18,
    fontWeight: "700",
  },
  statusChip: {
    backgroundColor: "#e8f0fe",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontWeight: "600",
    color: "#1a73e8",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  metaLabel: {
    color: "#666",
    fontWeight: "600",
  },
  metaValue: {
    fontWeight: "500",
  },
  itemRow: {
    flexDirection: "row",
    marginBottom: 16,
    borderBottomWidth: 1,
    borderColor: "#eee",
    paddingBottom: 16,
  },
  itemRowMobile: {
    flexDirection: "column",
  },
  productImage: {
    width: 90,
    height: 90,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: "#f0f0f0",
  },
  itemContent: {
    flex: 1,
  },
  itemCode: {
    fontWeight: "700",
    fontSize: 14,
  },
  itemName: {
    fontSize: 13,
    marginBottom: 6,
  },
  meta: {
    fontSize: 12,
    color: "#555",
  },
  appointment: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "600",
    color: "#1a73e8",
  },
});

export default CustomerOrderSummaryReport;
